import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { supabase } from '../../lib/supabase'
import { ALL_HOME_GENRES, getHomeGenrePrefs, toggleHomeGenre } from '../../lib/homePrefs'
import { ACHIEVEMENTS as ACHIEVEMENTS_DEF } from '../../lib/achievements'
import { ACHIEVEMENT_CATS } from '../../lib/achievements'
import { BADGE_DEFS } from '../../lib/badges'
import { BadgeCard } from '../BadgeCard'
import { requestPermission, subscribeToPush, resubscribeFresh } from '../../lib/push'
import { postFn } from '../../lib/postFn'
import { Chip, Tabs } from '../UI'

// ── Profile helper components ──────────────────────────────────────
export function SettingRow({ label, desc, control }) {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 0', borderTop:'1px solid var(--border-soft)' }}>
      <div style={{ flex:1, minWidth:0, paddingRight:12 }}>
        <div style={{ color:'var(--text-strong)', fontSize:13, fontWeight:500 }}>{label}</div>
        {desc && <div style={{ color:'var(--text-dim)', fontSize:11, marginTop:2 }}>{desc}</div>}
      </div>
      {control}
    </div>
  )
}

// Reel knob — a plain white circle etched with a tiny film-reel spool (hub +
// 4 sprocket holes) via layered radial-gradients. Same switch mechanic and
// on/off affordance as a standard toggle (still slides, still amber-when-on)
// so nothing about the interaction changes — just a subtle cinema-brand detail
// on a control that appears ~15 times across Settings. Considered swapping the
// whole control for the Clapboard icon and decided against it: the clapboard
// already means "mark watched" elsewhere in the app, reusing it for on/off
// prefs would blur two different actions, and a bespoke control loses the
// native button's built-in keyboard/aria support (Jason 2026-07-03).
export const REEL_KNOB_BG = `
  radial-gradient(circle at 50% 50%, rgba(0,0,0,0.18) 0 2px, transparent 2.4px),
  radial-gradient(circle at 29% 29%, rgba(0,0,0,0.12) 0 1px, transparent 1.4px),
  radial-gradient(circle at 71% 29%, rgba(0,0,0,0.12) 0 1px, transparent 1.4px),
  radial-gradient(circle at 29% 71%, rgba(0,0,0,0.12) 0 1px, transparent 1.4px),
  radial-gradient(circle at 71% 71%, rgba(0,0,0,0.12) 0 1px, transparent 1.4px),
  #fff
`

// Toggle — the one canonical switch (44×26, reel-knob). Handles role/aria for a11y,
// plus a `disabled` prop and automatic busy state when `onChange` returns a promise
// (so an async pref write dims + locks the control until it resolves). ToggleRow and
// every Settings switch render this instead of re-declaring the mechanic inline.
export function Toggle({ value, onChange, disabled, label }) {
  const [busy, setBusy] = useState(false)
  const off = disabled || busy
  const handle = async () => {
    if (off) return
    const r = onChange(!value)
    if (r && typeof r.then === 'function') {
      setBusy(true)
      try { await r } finally { setBusy(false) }
    }
  }
  return (
    <button role="switch" aria-checked={!!value} aria-label={typeof label === 'string' ? label : undefined}
      disabled={off} onClick={handle} style={{
      width:52, height:31, borderRadius:'var(--radius-pill)', padding:0,
      border: value ? '1px solid rgba(var(--amber-rgb),0.7)' : '1px solid rgba(var(--ink-rgb),0.12)',
      background: value ? 'var(--grad-amber)' : 'rgba(var(--ink-rgb),0.06)',
      // Inset well when off, amber halo when on (component standard 1b, 2026-07-25).
      boxShadow: value
        ? 'inset 0 1px 2px rgba(0,0,0,0.25), 0 0 18px -6px rgba(var(--amber-rgb),0.75)'
        : 'inset 0 1px 3px rgba(0,0,0,0.45)',
      cursor: busy ? 'wait' : off ? 'default' : 'pointer', position:'relative', flexShrink:0,
      transition:'background 0.22s var(--ease-out), box-shadow 0.22s var(--ease-out), border-color 0.22s var(--ease-out)',
      opacity: busy ? 0.6 : 1,
    }}>
      <div style={{
        width:25, height:25, borderRadius:'var(--radius-full)', background: REEL_KNOB_BG,
        position:'absolute', top:2, boxShadow:'0 1px 3px rgba(0,0,0,0.5)',
        transition:'left 0.26s var(--ease-out, ease-out), transform 0.38s var(--ease-out, ease-out)',
        left: value ? 24 : 2,
        transform: value ? 'rotate(360deg)' : 'rotate(0deg)',  // the reel spools up as it flips
      }} />
    </button>
  )
}

export function Field({ label, error, children }) {
  return (
    <div style={{ marginBottom:12 }}>
      <div style={{ color:'var(--text-dim)', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:5 }}>{label}</div>
      {children}
      {error && <div style={{ color:'var(--state-bad)', fontSize:11, marginTop:4 }}>{error}</div>}
    </div>
  )
}

export function SectionLabel({ children, amber }) {
  return <p style={{ color: amber ? 'var(--amber)' : 'var(--text-dim)', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', margin:'0 0 12px' }}>{children}</p>
}

// Sub-heading INSIDE a settings card, for grouping related toggles. Deliberately
// quieter than SectionLabel (which titles the whole card) so a card reads as one
// section with groups in it, not several competing headings.
export function GroupLabel({ children }) {
  return <p style={{ color:'var(--text-muted)', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.09em', margin:'0 0 8px' }}>{children}</p>
}

export const pageStyle = { minHeight:'100dvh', background:'var(--bg)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:24, fontFamily:'var(--font)', textAlign:'center' }
export const card = { background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:'16px', marginBottom:14 }
const statCard = { ...card, marginBottom:0, textAlign:'center', padding:'14px 0' }
export const inputStyle = { width:'100%', padding:'10px 12px', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', color:'var(--text)', fontSize:16, boxSizing:'border-box', fontFamily:'var(--font)' }
export const btnAmber = { background:'rgba(var(--amber-rgb),0.12)', border:'1px solid rgba(var(--amber-rgb),0.3)', borderRadius:'var(--radius)', color:'var(--amber)', fontWeight:600, fontSize:14, padding:'12px 18px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6, fontFamily:'var(--font)' }

export function AchievementTile({ ach, earned, progress, pct, idx, isOpen, onOpen }) {
  const almostThere = !earned && pct >= 60

  return (
    <div style={{ position:'relative', '--nxa':`achIn 0.4s ${idx * 45}ms cubic-bezier(0.34,1.15,0.64,1) both` }}>
      <div onClick={onOpen} style={{
        background: earned
          ? 'linear-gradient(135deg, rgba(var(--amber-rgb),0.14), rgba(var(--amber-rgb),0.06))'
          : almostThere ? 'rgba(var(--amber-rgb),0.04)' : 'var(--bg-card)',
        border: `1px solid ${earned ? 'rgba(var(--amber-rgb),0.4)' : almostThere ? 'rgba(var(--amber-rgb),0.2)' : 'var(--border)'}`,
        borderRadius:'var(--radius-md)', padding:'14px 8px 12px', textAlign:'center',
        boxShadow: earned ? '0 0 20px rgba(var(--amber-rgb),0.12), 0 2px 8px rgba(0,0,0,0.3)' : 'none',
        cursor:'pointer', transition:'all 0.2s', position:'relative', overflow:'hidden',
      }}>
        {/* Earned shimmer sweep */}
        {earned && (
          <div style={{ position:'absolute', inset:0, background:'linear-gradient(105deg, transparent 40%, rgba(var(--amber-rgb),0.06) 50%, transparent 60%)', '--nxa':'achShimmer 3s ease-in-out infinite', pointerEvents:'none' }}/>
        )}
        <div style={{
          fontSize:28, marginBottom:6,
          filter: earned ? 'none' : 'grayscale(0.9)',
          opacity: earned ? 1 : almostThere ? 0.5 : 0.3,
          transform: earned ? 'scale(1)' : 'scale(0.88)',
          transition:'all 0.3s',
          display:'block',
        }}>
          {earned ? ach.icon : '🔒'}
        </div>
        <div style={{
          color: earned ? 'var(--amber)' : almostThere ? 'rgba(var(--amber-rgb),0.55)' : 'var(--text-dim)',
          fontSize:9, fontWeight:700, lineHeight:1.3, letterSpacing:'0.02em',
        }}>
          {ach.label}
        </div>
        {earned && (
          <div style={{ position:'absolute', top:5, right:6, width:10, height:10, borderRadius:'var(--radius-full)', background:'rgba(var(--amber-rgb),0.4)', '--nxa':'achPulse 2.5s ease-in-out infinite' }}/>
        )}
        {!earned && ach.target > 0 && (
          <div style={{ marginTop:7, padding:'0 6px' }}>
            <div style={{ background:'var(--surface)', borderRadius:3, height:2.5, overflow:'hidden' }}>
              <div style={{ background: pct > 60 ? 'rgba(var(--amber-rgb),0.6)' : 'rgba(var(--amber-rgb),0.3)', borderRadius:3, height:'100%', width:`${pct}%`, transition:'width 0.8s ease' }}/>
            </div>
          </div>
        )}
      </div>

    </div>
  )
}

// `sections` lets Settings place each toggle under the right heading instead of
// rendering one flat block: episode alerts belong with episode alerts, not beside
// "Share my activity". Defaults to everything so any other caller is unaffected.
// The permission banner and the Test button live in 'banner', so rendering
// several PushSetups doesn't duplicate them.
export function PushSetup({ user, profile, onRefreshProfile, sections = ['banner', 'episodes', 'social', 'game'] }) {
  const has = (k) => sections.includes(k)
  const [permState,  setPermState]  = useState(typeof Notification !== 'undefined' ? Notification.permission : 'unsupported')
  const [subscribed, setSubscribed] = useState(false)
  const [testing,    setTesting]    = useState(false)
  const [testMsg,    setTestMsg]    = useState('')

  useEffect(() => {
    // Check if already subscribed
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      navigator.serviceWorker.ready.then(async reg => {
        let sub = await reg.pushManager.getSubscription()
        // Self-heal: permission granted but the local subscription went missing
        // (common after a PWA reinstall or service-worker update) — re-subscribe
        // so this device matches the server and the test button reappears.
        if (!sub && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          try { sub = await subscribeToPush(user.id) } catch (e) {}
        }
        setSubscribed(!!sub)
      })
    }
  }, [])

  const handleEnable = async () => {
    const perm = await requestPermission()
    setPermState(perm)
    if (perm === 'granted') {
      // Force a fresh endpoint on an explicit enable — the user is here BECAUSE push is
      // broken, so a dead-but-key-matching browser sub must be torn down, not reused.
      const sub = await resubscribeFresh(user.id)
      setSubscribed(!!sub)
      const prefs = { ...(profile?.notification_prefs||{}), same_day: true, receive_activity: true }
      await supabase.from('profiles').update({ notification_prefs: prefs }).eq('id', user.id)
      await onRefreshProfile()
    }
  }

  const sendTest = () => postFn('/.netlify/functions/notify-activity', {
    type: 'rated',
    show: { id: 1396, name: 'Breaking Bad', poster_path: '/ggFHVNu6YYI5L9pCfOacjizRGt.jpg' },
    extra: { rating: 5, label: 'All-Timer' },
    test: true,
  }).then(r => r.json())

  const handleTest = async () => {
    setTesting(true); setTestMsg('')
    try {
      const d = await sendTest()
      // Honest success = at least one device was actually reached. The server returns
      // ok:true even when the only subscription was dead (410 → pruned, sent:0) — the old
      // `d.sent >= 0` check reported "Test sent!" while nothing was delivered (Jason 2026-07-22).
      if (d.sent > 0) { setTestMsg('✓ Test sent! Check for a notification.'); setTesting(false); return }
      // Nothing delivered: this device's endpoint is dead/missing. Mint a fresh one and retry.
      setTestMsg('Re-linking this device…')
      const sub = await resubscribeFresh(user.id)
      if (!sub) { setSubscribed(false); setTestMsg('Couldn’t enable push here, tap “Turn on here,” and check iOS Settings → Notifications → NextEp is allowed.'); setTesting(false); return }
      setSubscribed(true)
      const d2 = await sendTest()
      setTestMsg(d2.sent > 0
        ? '✓ Re-linked and sent! Check for a notification.'
        : 'Re-linked this device. If nothing arrives, check iOS Settings → Notifications → NextEp.')
    } catch(e) { setTestMsg(`Error: ${e.message}`) }
    setTesting(false)
  }

  const isGranted  = permState === 'granted'
  const isDenied   = permState === 'denied'
  const isUnsup    = permState === 'unsupported'

  return (
    <div>
      {/* Status banner — 'banner' section ONLY. Gating just the Enable button left
          the surrounding card rendering once per PushSetup instance, so grouping
          the toggles put "Notifications blocked" on screen three times (caught in
          the signed-in screenshot, 2026-07-28). */}
      {has('banner') && <div style={{
        background: isGranted && subscribed
          ? 'color-mix(in srgb, var(--state-good) 8%, transparent)'
          : isUnsup
          ? 'var(--bg-card)'
          : 'rgba(var(--amber-rgb),0.06)',
        border: `1px solid ${
          isGranted && subscribed ? 'color-mix(in srgb, var(--state-good) 25%, transparent)'
          : isUnsup ? 'var(--border)'
          : 'rgba(var(--amber-rgb),0.2)'}`,
        borderRadius:'var(--radius)', padding:'12px 14px', marginBottom:14,
        display:'flex', alignItems:'center', gap:12,
      }}>
        <div style={{ fontSize:22 }}>
          {isGranted && subscribed ? '🔔' : isDenied ? '🔕' : isUnsup ? '📱' : '🔔'}
        </div>
        <div style={{ flex:1 }}>
          <div style={{ color: isUnsup ? 'var(--text-muted)' : 'var(--text)', fontWeight:600, fontSize:13 }}>
            {isGranted && subscribed ? 'Push notifications active'
              : isGranted ? 'Push paused on this device'
              : isDenied ? 'Notifications blocked'
              : isUnsup  ? 'Install app for notifications'
              : 'Enable push notifications'}
          </div>
          <div style={{ color:'var(--text-muted)', fontSize:11, marginTop:2, lineHeight:1.4 }}>
            {isGranted && subscribed ? "Episode alerts and activity updates are on"
              : isDenied ? 'Go to browser Settings → Site Settings → Allow'
              : isUnsup  ? 'Tap Share → "Add to Home Screen" in Safari, then open from your home screen to enable push'
              : 'Get notified when shows air and followers share activity'}
          </div>
        </div>
        {!isDenied && !isUnsup && !(isGranted && subscribed) && (
          <button onClick={handleEnable} style={{ background:'var(--amber)', border:'none', borderRadius:'var(--radius-sm)', color:'var(--amber-ink)', fontWeight:700, fontSize:13, padding:'8px 14px', cursor:'pointer', fontFamily:'var(--font)', flexShrink:0 }}>
            {isGranted ? 'Turn on here' : 'Enable'}
          </button>
        )}
      </div>}

      {/* Individual toggles, placed by `sections` (see the prop comment). */}
      {has('episodes') && <ToggleRow
        label="Episode reminders"
        sub="Notify when shows on your list air today, and when a show you finished comes back"
        value={profile?.notification_prefs?.same_day !== false}
        onChange={async v => {
          const prefs = { ...(profile?.notification_prefs||{}), same_day: v }
          await supabase.from('profiles').update({ notification_prefs: prefs }).eq('id', user.id)
          await onRefreshProfile()
        }}
      />}
      {has('social') && <ToggleRow
        label="Share my activity"
        sub="Let followers see your ratings, progress & completions"
        value={!!profile?.notification_prefs?.share_activity}
        onChange={async v => {
          const prefs = { ...(profile?.notification_prefs||{}), share_activity: v }
          await supabase.from('profiles').update({ notification_prefs: prefs }).eq('id', user.id)
          await onRefreshProfile()
        }}
      />}
      {has('social') && <ToggleRow
        label="Receive activity from following"
        sub="Get notified when followed users rate or finish shows"
        value={profile?.notification_prefs?.receive_activity !== false}
        onChange={async v => {
          const prefs = { ...(profile?.notification_prefs||{}), receive_activity: v }
          await supabase.from('profiles').update({ notification_prefs: prefs }).eq('id', user.id)
          await onRefreshProfile()
        }}
      />}

      {has('game') && <ToggleRow
        label='Daily games'
        sub='Streak nudges for Cliffhanger and Face-Off, and a heads-up when people you follow are playing'
        value={profile?.notification_prefs?.daily_game !== false}
        onChange={async v => {
          const prefs = { ...(profile?.notification_prefs||{}), daily_game: v }
          await supabase.from('profiles').update({ notification_prefs: prefs }).eq('id', user.id)
          await onRefreshProfile()
        }}
      />}
      {/* Test button - only when subscribed */}
      {has('banner') && isGranted && (
        <div style={{ marginTop:12 }}>
          <button onClick={handleTest} disabled={testing} style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', color:'var(--text-muted)', fontWeight:500, fontSize:12, padding:'7px 14px', cursor:'pointer', fontFamily:'var(--font)' }}>
            {testing ? 'Sending…' : '🧪 Send test notification'}
          </button>
          {testMsg && <div style={{ color: testMsg.startsWith('✓') ? 'var(--green)' : 'var(--state-bad)', fontSize:12, marginTop:6 }}>{testMsg}</div>}
        </div>
      )}
    </div>
  )
}

export function HomeGenrePicker() {
  const [prefs, setPrefs] = useState(() => getHomeGenrePrefs())

  const toggle = (id) => {
    const updated = toggleHomeGenre(id)
    setPrefs(new Set(updated))
    // Notify Home page to refresh
    window.dispatchEvent(new Event('homeGenresUpdated'))
  }

  const enabledCount = prefs.size

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
        <p style={{ color:'var(--text-muted)', fontSize:12, fontWeight:400, margin:0, lineHeight:1.4, paddingRight:10 }}>
          Choose which genre rows appear on your home page
        </p>
        <span style={{ color:'var(--text-dim)', fontSize:11 }}>{enabledCount} active</span>
      </div>
      <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
        {ALL_HOME_GENRES.map(g => {
          const on = prefs.has(g.id)
          return (
            <Chip key={g.id} active={on} onClick={() => toggle(g.id)} style={{
              padding:'6px 12px', fontWeight: on ? 700 : 500,
              color: on ? 'var(--amber)' : 'var(--text-dim)',
              transition:'all 0.15s', display:'flex', alignItems:'center', gap:5,
            }}>
              <span style={{ fontSize:14 }}>{g.emoji}</span> {g.label}
              {on && <span style={{ fontSize:10, opacity:0.7 }}>✓</span>}
            </Chip>
          )
        })}
      </div>
      <p style={{ color:'var(--text-dim)', fontSize:11, margin:'10px 0 0', lineHeight:1.5 }}>
        Changes apply instantly.
      </p>
    </div>
  )
}

export function ToggleRow({ label, sub, value, onChange }) {
  // The LABEL AREA toggles too, not just the ~50px switch. Settings has ~10 of these
  // and every one of them used to demand a precise hit on the switch itself — the
  // cramped-tap-target problem Jason keeps (rightly) flagging. Scoped to the text
  // block rather than the whole row on purpose: the switch is a sibling, so a tap on
  // it can't also bubble up and toggle a second time back to where it started.
  const toggle = () => onChange(!value)
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 0', borderBottom:'1px solid var(--border)' }}>
      <div
        role="button"
        tabIndex={0}
        aria-pressed={!!value}
        onClick={toggle}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle() } }}
        style={{ flex:1, minWidth:0, paddingRight:12, cursor:'pointer', WebkitTapHighlightColor:'transparent' }}
      >
        <div style={{ color:'var(--text)', fontSize:13, fontWeight:500 }}>{label}</div>
        <div style={{ color:'var(--text-muted)', fontSize:11, marginTop:2 }}>{sub}</div>
      </div>
      <Toggle value={value} onChange={onChange} label={typeof label === 'string' ? label : undefined} />
    </div>
  )
}

// Badge collection — interactive flip cards (BadgeCard), same trading-card
// language as Cinephile Cards. Earned badges lead the row so the identity
// markers you actually have read first; locked ones trail, greyed, with a
// progress bar where the badge has a clean numeric target (see badgeProgress
// in lib/badges.js). Sits above the Achievements grid in the same tab — badges
// deep-link here already (BadgeRow's "+N more"), so this is where the fuller
// set lives rather than a separate page.
export function BadgeGrid({ earnedBadges, progress = {} }) {
  const ids = Object.keys(BADGE_DEFS)
  const sorted = [...ids].sort((a, b) => {
    const ea = earnedBadges.has(a), eb = earnedBadges.has(b)
    if (ea !== eb) return ea ? -1 : 1
    return 0
  })
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 2 }}>
        <SectionLabel>Badges</SectionLabel>
        <span style={{ color: 'var(--text-dim)', fontSize: 11, fontWeight: 600 }}>{earnedBadges.size} / {ids.length}</span>
      </div>
      {/* Badges (who you are) and Achievements (milestones you've hit) sit right next to
          each other on this tab and can otherwise read as the same thing twice — this line
          is the whole fix, one sentence of framing instead of a structural rework. */}
      <div style={{ color: 'var(--text-dim)', fontSize: 11, marginBottom: 14 }}>Who you are as a viewer.</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', rowGap: 20, columnGap: 8 }}>
        {sorted.map(id => (
          <div key={id} style={{ display: 'flex', justifyContent: 'center' }}>
            <BadgeCard def={BADGE_DEFS[id]} earned={earnedBadges.has(id)} progress={progress[id]} size="small" />
          </div>
        ))}
      </div>
    </div>
  )
}

export function AchievementGrid({ unlocked, totalAdds, totalRatings, totalNotes, totalCompleted = 0, totalFollows = 0,
  totalStreak = 0, totalRewatches = 0, totalCards = 0, totalCardsLegendary = 0, totalBingeDay = 0 }) {
  const [openAch, setOpenAch] = useState(null)
  // Category tab, default to the one with the most recent progress so the tab a
  // user lands on isn't always the same "Collection" — falls back to it below.
  const [cat, setCat] = useState('Collection')
  const total   = ACHIEVEMENTS_DEF.length
  const earnedN = ACHIEVEMENTS_DEF.filter(a => unlocked.has(a.id)).length
  const overallPct = Math.round((earnedN / total) * 100)

  // Progress count for an achievement type. Covers every scalar count available
  // in Profile. NOT available here: `quests` (fed from a ref in App.jsx) and the
  // per-genre `genre_*` counts — those fall through to 0, so their locked tiles
  // show the earned/locked state but no partial progress bar.
  const countFor = (type) => {
    switch (type) {
      case 'adds':            return totalAdds
      case 'ratings':         return totalRatings
      case 'completed':       return totalCompleted
      case 'follows':         return totalFollows
      case 'notes':           return totalNotes
      case 'streak':          return totalStreak
      case 'rewatches':       return totalRewatches
      case 'cards':           return totalCards
      case 'cards_legendary': return totalCardsLegendary
      case 'binge_day':       return totalBingeDay
      default:                return 0
    }
  }

  // Find the achievement closest to unlocking (highest pct among locked)
  const closest = ACHIEVEMENTS_DEF
    .filter(a => !unlocked.has(a.id) && a.target > 0)
    .map(a => {
      const prog = countFor(a.type)
      return { ...a, prog, pct: Math.min(100, Math.round((prog/a.target)*100)) }
    })
    .sort((a,b) => b.pct - a.pct)[0]

  return (
    <div>
      {/* Progress summary */}
      <div style={{ marginBottom:16 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:6 }}>
          <span style={{ color:'var(--text-dim)', fontSize:11, fontWeight:600 }}>
            {earnedN} / {total} earned
          </span>
          <span style={{ color:'var(--amber)', fontSize:11, fontWeight:600 }}>{overallPct}%</span>
        </div>
        <div style={{ background:'var(--surface)', borderRadius:6, height:5, overflow:'hidden' }}>
          <div style={{ background:'linear-gradient(90deg, var(--amber), rgba(var(--amber-rgb),0.6))', borderRadius:6, height:'100%', width:`${overallPct}%`, transition:'width 0.8s var(--ease-spring)' }}/>
        </div>
      </div>

      {/* Closest to unlocking, spotlight — tap to jump the tab strip to its category */}
      {closest && closest.pct >= 40 && (
        <div onClick={() => setCat(closest.cat)} style={{ cursor:'pointer', background:'rgba(var(--amber-rgb),0.06)', border:'1px solid rgba(var(--amber-rgb),0.2)', borderRadius:'var(--radius)', padding:'10px 14px', marginBottom:14, display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:22 }}>⚡</span>
          <div style={{ flex:1 }}>
            <div style={{ color:'var(--amber)', fontSize:11, fontWeight:700 }}>Almost there, {closest.label}</div>
            <div style={{ color:'var(--text-dim)', fontSize:10, marginTop:1 }}>{closest.prog}/{closest.target} · {closest.pct}% complete</div>
          </div>
        </div>
      )}

      {/* Category tabs — replaced one long flat grid (~40 tiles) with a tabbed,
          per-category view (Jason's "Achievements grid improvements" mock,
          2026-08-15). Reuses the house Tabs primitive rather than hand-rolling
          another pill bar. */}
      <Tabs
        tabs={ACHIEVEMENT_CATS.map(c => ({
          value: c, label: c,
          badge: ACHIEVEMENTS_DEF.filter(a => a.cat === c && unlocked.has(a.id)).length || undefined,
        }))}
        value={cat} onChange={setCat} size={11.5}
        style={{ marginBottom: 14 }}
      />

      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10 }}>
        {ACHIEVEMENTS_DEF.filter(a => a.cat === cat).map((ach, idx) => {
          const earned = unlocked.has(ach.id)
          const progress = countFor(ach.type)
          const pct = ach.target > 0 ? Math.min(100, Math.round((progress/ach.target)*100)) : (earned ? 100 : 0)
          return <AchievementTile key={ach.id} ach={ach} earned={earned} progress={progress} pct={pct} idx={idx}
            isOpen={openAch?.id === ach.id} onOpen={() => setOpenAch({ ach, earned, progress, pct })}/>
        })}
      </div>

      {/* Single shared modal, only one can be open at a time */}
      {openAch && createPortal((
        <div onClick={() => setOpenAch(null)} style={{
          position:'fixed', inset:0, zIndex:'var(--z-modal)', display:'flex', alignItems:'center', justifyContent:'center',
          background:'rgba(var(--bg-rgb),0.75)', backdropFilter:'blur(10px)',
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background:'var(--sheet)',
            border:`1px solid ${openAch.earned ? 'rgba(var(--amber-rgb),0.4)' : 'var(--border)'}`,
            borderRadius:'var(--radius-xl)', padding:'28px 24px', maxWidth:300, width:'88vw',
            boxShadow:'0 16px 48px rgba(0,0,0,0.6)',
            '--nxa':'achPop 0.3s var(--ease-spring)',
            position:'relative',
          }}>
            <button onClick={() => setOpenAch(null)} aria-label="Close" style={{ position:'absolute', top:12, right:14, background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-full)', width:28, height:28, color:'var(--text-dim)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14 }}>×</button>
            <div style={{ fontSize:48, textAlign:'center', marginBottom:14, filter: openAch.earned ? 'none' : 'grayscale(0.7)' }}>
              {openAch.earned ? openAch.ach.icon : '🔒'}
            </div>
            <div style={{ color: openAch.earned ? 'var(--amber)' : 'var(--text-strong)', fontWeight:800, fontSize:18, textAlign:'center', marginBottom:8 }}>
              {openAch.earned && '✓ '}{openAch.ach.label}
            </div>
            <div style={{ color:'var(--text-muted)', fontSize:13, textAlign:'center', lineHeight:1.55, marginBottom: !openAch.earned && openAch.ach.target > 0 ? 18 : 0 }}>
              {openAch.ach.desc}
            </div>
            {!openAch.earned && openAch.ach.target > 0 && (
              <div>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:7 }}>
                  <span style={{ color:'var(--text-dim)', fontSize:11 }}>Progress</span>
                  <span style={{ color:'var(--amber)', fontSize:11, fontWeight:600 }}>{openAch.progress}/{openAch.ach.target}</span>
                </div>
                <div style={{ background:'var(--surface)', borderRadius:6, height:6, overflow:'hidden' }}>
                  <div style={{ background:'var(--amber)', borderRadius:6, height:'100%', width:`${openAch.pct}%`, transition:'width 0.6s ease' }}/>
                </div>
              </div>
            )}
            {openAch.earned && <div style={{ color:'rgba(var(--amber-rgb),0.45)', fontSize:11, textAlign:'center', marginTop:12 }}>Achievement unlocked 🎉</div>}
          </div>
        </div>
      ), document.body)}

      <style>{`
        @keyframes achIn      { from{opacity:0;transform:scale(0.8) translateY(12px)} to{opacity:1;transform:scale(1) translateY(0)} }
        @keyframes achShimmer { 0%{transform:translateX(-100%)} 100%{transform:translateX(200%)} }
        @keyframes achPulse   { 0%,100%{opacity:0.4;transform:scale(1)} 50%{opacity:1;transform:scale(1.5)} }
        @keyframes achPop     { from{opacity:0;transform:scale(0.85)} to{opacity:1;transform:scale(1)} }
      `}</style>
    </div>
  )
}
